import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import { Button, Flex, Modal, Space, Tag, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import {
  clonePrizeManagement,
  editPrizeManagement,
  getPrizeManagement,
} from '@/services/prize_management.service';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatPeriod, columnSorter, formatNumber } from '@/utils/formatters';
import { useMutation } from '@tanstack/react-query';
import usePrizeManagement from '@/redux/hooks/usePrizeManagement';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { ErrorResponseModal, SuccessResponseModal } from '@/components/AppModal/modals';
import AppDropdown from '@/components/AppDropdown';
import { getActiveBadge } from '@/utils/getActiveBadge';

const rowKey = 'key';
const DEFAULT_DATE = dayjs().format('YYYY-MM-DD');

const activeOptions = [
  { value: null, label: 'All' },
  { value: 1, label: 'Active' },
  { value: 2, label: 'Inactive' },
];
function PrizeManagement() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [queryValues, setQueryValues] = useState({
    name: null,
    isActive: null,
    startTime: null,
  });
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const { setPrizeValue } = usePrizeManagement();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // set default start time if not set in search params
    if (!searchParams.get('start_time')) {
      setSearchParams(`?start_time=${DEFAULT_DATE}`);
      setStartTime(DEFAULT_DATE);
      setQueryValues((prev) => ({
        ...prev,
        startTime: DEFAULT_DATE,
      }));
    }
  }, [searchParams, setSearchParams, setStartTime, setQueryValues]);

  useEffect(() => {
    const startTimeParam = searchParams.get('start_time') || DEFAULT_DATE;
    setStartTime(startTimeParam);
    setQueryValues((prev) => ({
      ...prev,
      startTime: startTimeParam,
    }));
  }, [searchParams]);

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getPrizeManagement({
        ...queryValues,
      }),
    ['prizeManagement', queryValues],
    {
      enabled: !!startTime,
    }
  );

  const { mutate: toggleActive } = useMutation({
    mutationFn: editPrizeManagement,
  });

  const { mutate: clone, isPending: isCloning } = useMutation({
    mutationFn: clonePrizeManagement,
  });

  const onEditAction = (record) => {
    setPrizeValue({
      ...record,
      period: `${formatPeriod(record.start_date, record.end_date)}`,
    });
    navigate(PATH.PRIZE_MANAGEMENT.UPDATE.replace(':id', record.id), {
      state: {
        previousSearchParams: Object.fromEntries(searchParams.entries()),
        returnUrl: `${PATH.PRIZE_MANAGEMENT.ROOT}?${searchParams.toString()}`,
      },
    });
  };

  const totalNumPrize = useMemo(() => {
    return data?.reduce((total, item) => total + item.num_prize, 0) || 0;
  }, [data]);

  const columns = [
    {
      title: 'No. ',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      sorter: columnSorter('id'),
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Prize Name',
      dataIndex: 'name',
      key: 'name',
      sorter: columnSorter('name'),
      render: (value) => (value ? value : '-'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (+type === 1 ? 'Topup' : 'Physical'),
      sorter: columnSorter('type'),
    },
    {
      title: 'Prize Quantity',
      dataIndex: 'num_prize',
      key: 'num_prize',
      sorter: columnSorter('num_prize'),
      render: (value) => (value ? formatNumber(value) : '0'),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) =>
        getActiveBadge(+isActive === 1, {
          activeText: 'Active',
          inactiveText: 'Inactive',
        }),
      sorter: columnSorter('is_active'),
    },
    {
      title: 'Percentage',
      dataIndex: 'percent',
      key: 'percentage',
      // render: (record) => {
      //   const percentage = record.num_prize
      //     ? ((record.num_prize / totalNumPrize) * 100).toFixed(2)
      //     : 0;
      //   return `${percentage}%`;
      // },
      render: (percent) => (percent ? `${percent}%` : '0%'),
      // sorter: (a, b) => {
      //   const percentageA = a.num_prize ? (a.num_prize / totalNumPrize) * 100 : 0;
      //   const percentageB = b.num_prize ? (b.num_prize / totalNumPrize) * 100 : 0;
      //   return percentageA - percentageB;
      // },
    },
    {
      title: 'ISDN Win',
      dataIndex: 'isdn_win',
      key: 'isdn_win',
      width: 265,
      render: (value) =>
        value ? (
          <Flex align="center" wrap="wrap" gap={4} justify="center" style={{}}>
            {value?.split(',').map((isdn) => (
              <Tag key={isdn} color="blue" className="">
                {isdn}
              </Tag>
            ))}
          </Flex>
        ) : (
          '-'
        ),
    },
    {
      title: 'ISDN Block',
      dataIndex: 'isdn_block',
      key: 'isdn_block',
      width: 265,
      render: (value) =>
        value ? (
          <Flex align="center" wrap="wrap" gap={4} justify="center" style={{}}>
            {value?.split(',').map((isdn) => (
              <Tag key={isdn} color="default" className="">
                {isdn}
              </Tag>
            ))}
          </Flex>
        ) : (
          '-'
        ),
    },
    {
      title: 'Period',
      key: 'period',
      render: (record) => {
        return formatPeriod(record.start_date, record.end_date);
      },
    },
  ];

  const handleOnCreate = () => {
    navigate(PATH.PRIZE_MANAGEMENT.CREATE);
  };

  const handleQuery = () => {
    setQueryValues({
      name: searchValue,
      isActive:
        (selectedFilter === 0 || selectedFilter >= 0) && selectedFilter !== null
          ? +selectedFilter
          : null,
      startTime,
    });

    setSearchParams(`?start_time=${startTime}`);
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setQueryValues({
      name: null,
      isActive: null,
      startTime: DEFAULT_DATE,
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleClone = () => {
    Modal.confirm({
      title: 'Clone Prizes',
      content: "This will clone the current month's prizes to the next month. Continue?",
      okText: isCloning ? 'Cloning...' : 'Clone',
      cancelText: 'Cancel',
      okButtonProps: {
        loading: isCloning,
        disabled: isCloning,
      },
      cancelButtonProps: {
        disabled: isCloning,
      },
      onOk: () => {
        clone(_, {
          onSuccess: () => {
            refetch();
            SuccessResponseModal({
              title: 'Clone Successful',
              message: 'Prizes have been successfully cloned to the next month.',
            });
          },
          onError: (error) => {
            const errorMessage = error?.data?.errorMessage || 'Something went wrong';
            ErrorResponseModal({
              title: 'Clone Failed',
              message: errorMessage,
            });
          },
        });
      },
    });
  };

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Prize Management" />
      <AppTableQuery
        customActionButtons={
          <Tooltip title="Clone the current month's prizes to next month">
            <Button onClick={handleClone} loading={isCloning} disabled={isCloning}>
              <span className="text-sm">{isCloning ? 'Cloning...' : 'Clone'}</span>
            </Button>
          </Tooltip>
        }
        hasDatePicker
        searchPlaceholder="Prize Name"
        filterPlaceholder="Active"
        filterLabel="Status"
        filterOptions={activeOptions}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        time={startTime}
        timePlaceholder="Time"
        setTime={setStartTime}
        handleQuery={handleQuery}
        handleReset={handleReset}
        allowClear={false}
        canDisableFutureDate={false}
      />
      <AppTable
        columns={columns}
        dataSource={data || []}
        rowKey={rowKey}
        loading={isLoading || isRefetching}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        hasPagination={false}
        hasRowAction
        onCreate={handleOnCreate}
        onRefresh={handleRefresh}
        rowAction={(record) => {
          return (
            <AppDropdown
              options={[
                {
                  key: '1',
                  label: 'Edit',
                  onClick: () => onEditAction(record),
                },
                {
                  key: '2',
                  label: `Mark as ${+record.is_active === 1 ? 'Inactive' : 'Active'}`,
                  onClick: () => {
                    toggleActive(
                      {
                        id: record.id,
                        is_active: +record.is_active === 1 ? 2 : 1,
                      },
                      {
                        onSuccess: () => {
                          refetch();
                          toast.success(
                            `Prize ${record.name} is now ${
                              +record.is_active === 1 ? 'inactive' : 'active'
                            }`,
                            {
                              autoClose: 3000,
                              hideProgressBar: true,
                            }
                          );
                        },
                        onError: (error) => {
                          const errorMessage = error?.data?.errorMessage || 'Something went wrong';
                          toast.error(errorMessage, {
                            autoClose: 3000,
                            hideProgressBar: true,
                          });
                        },
                      }
                    );
                  },
                },
              ]}
              onChange={(item) => item.onClick()}
              placement="bottomRight"
              overlayStyle={{ minWidth: '150px' }}
            >
              <Button type="text" className="px-2">
                <EllipsisOutlined />
              </Button>
            </AppDropdown>
          );
        }}
      />
    </Space>
  );
}

export default PrizeManagement;
